// Positions a dropdown's `.list-wrapper` after it has been teleported to
// <body>. Since the panel is no longer a DOM descendant of the trigger, its
// geometry has to be computed from the trigger's own bounding rect (passed
// via an explicit `ref="anchor"`) rather than derived from `parentElement`.
export default {
    data() {
        return {
            listStyle: null,
            isListFlipped: false,
        };
    },
    watch: {
        isListOpen(open) {
            if (open) {
                this.onListOpened();
            } else {
                this.onListClosed();
            }
        },
    },
    beforeUnmount() {
        this.onListClosed();
    },
    methods: {
        stepHighlighted(options, currentValue, delta) {
            if (!options.length) {
                return currentValue;
            }

            const currentIndex = options.indexOf(currentValue);
            if (currentIndex === -1) {
                return options[0];
            }

            const nextIndex = Math.min(
                Math.max(currentIndex + delta, 0),
                options.length - 1
            );

            return options[nextIndex];
        },
        updateListPosition() {
            const el = this.$refs.anchor;
            if (!el) {
                return;
            }

            const rect = el.getBoundingClientRect();
            const viewportWidth = document.documentElement.clientWidth;
            const viewportHeight = document.documentElement.clientHeight;
            const visualViewport = window.visualViewport;
            const visibleTop = visualViewport?.offsetTop ?? 0;
            const visibleBottom =
                visibleTop + (visualViewport?.height ?? viewportHeight);

            // The old rule was `position: absolute` inside .dropdown (a
            // content-box element with a 1px border), so its containing
            // block was the padding box — hence the client* offsets, which
            // reproduce the previous `top: 0; left: -1px; width: 100.5%`.
            const top = rect.top + el.clientTop;
            let left = rect.left + el.clientLeft - 1;
            const width = el.clientWidth * 1.005;

            left = Math.min(left, viewportWidth - width);
            left = Math.max(left, 0);

            const spaceBelow = visibleBottom - rect.bottom;
            const spaceAbove = rect.top - visibleTop;
            const flipped = spaceBelow < 200 && spaceAbove > spaceBelow;
            this.isListFlipped = flipped;

            const maxHeight = Math.max(
                (flipped ? spaceAbove : spaceBelow) - 8,
                120
            );

            const style = {
                left: `${left}px`,
                width: `${width}px`,
                "--dd-list-max-height": `${maxHeight}px`,
            };

            if (flipped) {
                style.bottom = `${viewportHeight - rect.top - 1}px`;
            } else {
                style.top = `${top}px`;
            }

            this.listStyle = style;
        },
        onListOpened() {
            // The anchor is already in the DOM by the time isListOpen flips,
            // so this can run synchronously — deferring to $nextTick would
            // leave `listStyle` null for a frame, and an unpositioned
            // `position: fixed` panel falls back to the top-left of <body>.
            this.updateListPosition();

            window.addEventListener("scroll", this.onViewportChange, true);
            window.addEventListener("resize", this.onViewportChange);
            window.visualViewport?.addEventListener(
                "resize",
                this.onVisualViewportChange
            );
            window.visualViewport?.addEventListener(
                "scroll",
                this.onVisualViewportChange
            );
        },
        onListClosed() {
            window.removeEventListener("scroll", this.onViewportChange, true);
            window.removeEventListener("resize", this.onViewportChange);
            window.visualViewport?.removeEventListener(
                "resize",
                this.onVisualViewportChange
            );
            window.visualViewport?.removeEventListener(
                "scroll",
                this.onVisualViewportChange
            );
            this.listStyle = null;
            this.isListFlipped = false;
        },
        onViewportChange(event) {
            // Scrolling inside the panel's own overflow shouldn't close it.
            if (
                event.target instanceof Node &&
                this.$refs.list &&
                this.$refs.list.contains(event.target)
            ) {
                return;
            }

            if (
                event.type === "resize" ||
                this.$refs.anchor?.contains(document.activeElement)
            ) {
                this.updateListPosition();
                return;
            }

            this.onCloseList();
        },
        onVisualViewportChange() {
            this.updateListPosition();
        },
    },
};
